import React from 'react';
import * as numeral from 'numeral';
import { Button } from '@lendi-ui/button';
import Field from '@lendi-ui/field';
import { Calendar, KeyboardArrowLeft, Message } from '@lendi-ui/icon';
import { Input } from '@lendi-ui/text-input';
import { Body, Heading } from '@lendi-ui/typography';
import { useCommPanel } from '@lendi/navbar';
import {
  calculateInterestOnlyMonthlyRepayment,
  calculatePrincipalInterestMonthlyRepayment,
  eventTracking,
  TextInputAttrs,
  useTextInput,
} from '../../utils';
import {
  CalculateButton,
  CtaSection,
  InputExtra,
  InputGroup,
  MoreCtas,
  QuoteAmount,
  QuotePeriod,
  QuoteSection,
  ResetButton,
  ResultsWrapper,
  Title,
  Wrapper,
} from './index.style';

interface InputsViewProps {
  loanAmount: TextInputAttrs;
  interestRate: TextInputAttrs;
  loanTerm: TextInputAttrs;
  interestOnlyPeriod: TextInputAttrs;
  isInterestOnly: {
    isInterestOnly: boolean;
    setIsInterestOnly(v: boolean): void;
  };
  calculateMonthlyRepayment(): void;
}
const InputsView = ({
  loanAmount,
  interestRate,
  loanTerm,
  interestOnlyPeriod,
  isInterestOnly: { isInterestOnly, setIsInterestOnly },
  calculateMonthlyRepayment,
}: InputsViewProps) => (
  <form
    onSubmit={(e) => {
      e.preventDefault();
      calculateMonthlyRepayment();
    }}
  >
    <Title>Calculate your loan repayments</Title>
    <InputGroup>
      <Field size="sm" label="How much do you want to borrow?">
        <Input {...loanAmount} before={<InputExtra side="left">$</InputExtra>} isFullWidth={true} />
      </Field>
      <Field size="sm" label="With an interest rate of">
        <Input {...interestRate} after={<InputExtra side="right">%</InputExtra>} isFullWidth={true} />
      </Field>
      {isInterestOnly ? (
        <Field size="sm" label="Interest only period">
          <Input {...interestOnlyPeriod} after={<InputExtra side="right">years</InputExtra>} isFullWidth={true} />
        </Field>
      ) : (
        <Field size="sm" label="Over a loan term of">
          <Input {...loanTerm} after={<InputExtra side="right">years</InputExtra>} isFullWidth={true} />
        </Field>
      )}
    </InputGroup>
    <InputGroup>
      <Field size="sm" label="Repayment type">
        <>
          <label>
            <input
              type="radio"
              value="principalAndInterest"
              checked={!isInterestOnly}
              onChange={() => setIsInterestOnly(false)}
            />{' '}
            Principal &amp; interest
          </label>
          <label>
            <input
              type="radio"
              value="interestOnly"
              checked={isInterestOnly}
              onChange={() => setIsInterestOnly(true)}
            />{' '}
            Interest only
          </label>
        </>
      </Field>
      <CalculateButton
        type="submit"
        variant="primary"
        onClick={() => {
          eventTracking('above-fold', 'navigate', 'Calculate loan payment');
        }}
        isDisabled={
          loanAmount.value === '0' ||
          interestRate.value === '0.0' ||
          (isInterestOnly && interestOnlyPeriod.value === '0') ||
          (!isInterestOnly && loanTerm.value === '0')
        }
      >
        Calculate loan payment
      </CalculateButton>
    </InputGroup>
  </form>
);

interface ResultsViewProps {
  monthlyRepayment: Numeral;
  interestRate: Numeral;
  loanTerm: Numeral;
  interestOnlyPeriod: Numeral;
  isInterestOnly: boolean;
  resetCalculator(): void;
}
const ResultsView = ({
  monthlyRepayment,
  interestRate,
  loanTerm,
  interestOnlyPeriod,
  isInterestOnly,
  resetCalculator,
}: ResultsViewProps) => {
  const { openChat } = useCommPanel();
  return (
    <>
      <ResetButton onClick={resetCalculator}>
        <KeyboardArrowLeft color="primary.500" /> Start again
      </ResetButton>
      <ResultsWrapper>
        <QuoteSection>
          <Title>Your repayments could be</Title>
          <QuoteAmount>$ {monthlyRepayment.format()}</QuoteAmount>
          <QuotePeriod>/ month</QuotePeriod>
          <Body size="sm" mt="xs">
            At an interest rate of {interestRate.format('0.00')}% over{' '}
            {isInterestOnly ? interestOnlyPeriod.format() : loanTerm.format()} years
          </Body>
          <Body size="xs" mt="lg" color="shade.500">
            <strong>Disclaimer:</strong> While every effort has been made to ensure the accuracy of this calculator, the
            actual results may vary depending on a variety of factors. This result is an indication only.
          </Body>
        </QuoteSection>
        <CtaSection>
          <Heading size="xs" mt="nil" mb="xs">
            Want to pay less each month on your home loan?
          </Heading>
          <Body size="sm" mb="md">
            We search through thousands of home loans to find the right one for you.
          </Body>
          <Button
            variant="primary"
            isFullWidth={true}
            href="/home-loans/search"
            onClick={() => {
              eventTracking('above-fold', 'navigate', 'Find a home loan now');
            }}
          >
            Find a home loan now
          </Button>
          <MoreCtas>
            <Button
              variant="empty"
              size="sm"
              onClick={() => {
                openChat();
                eventTracking('above-fold', 'navigate', 'Chat now');
              }}
            >
              <Message color="primary.500" /> Chat now
            </Button>
            <Button
              variant="empty"
              size="sm"
              href="/appointment-booking/postcode"
              onClick={() => {
                eventTracking('above-fold', 'navigate', 'Book an appointment');
              }}
            >
              <Calendar color="primary.500" /> Book an appointment
            </Button>
          </MoreCtas>
        </CtaSection>
      </ResultsWrapper>
    </>
  );
};

export const HomeLoanRepaymentCalculator = () => {
  const loanAmount = useTextInput(numeral(350000), numeral, (v) => v.format());
  const interestRate = useTextInput(numeral(3.69), numeral, (v) => v.format('0.00'));
  const loanTerm = useTextInput(numeral(30), numeral, (v) => v.format());
  const interestOnlyPeriod = useTextInput(numeral(1), numeral, (v) => v.format());
  const [isInterestOnly, setIsInterestOnly] = React.useState(false);
  const [isResultsView, setIsResultsView] = React.useState(false);
  const [monthlyRepayment, setMonthlyRepayment] = React.useState(0);

  function calculateMonthlyRepayment() {
    setMonthlyRepayment(
      isInterestOnly
        ? calculateInterestOnlyMonthlyRepayment(loanAmount.value.value(), interestRate.value.value())
        : calculatePrincipalInterestMonthlyRepayment(
            loanAmount.value.value(),
            interestRate.value.value(),
            loanTerm.value.value()
          )
    );
    setIsResultsView(true);
  }

  function resetCalculator() {
    loanAmount.update(numeral(350000));
    interestRate.update(numeral(3.69));
    loanTerm.update(numeral(30));
    interestOnlyPeriod.update(numeral(1));
    setIsInterestOnly(false);
    setIsResultsView(false);
    setMonthlyRepayment(0);
  }

  return (
    <Wrapper>
      {isResultsView ? (
        <ResultsView
          monthlyRepayment={numeral(monthlyRepayment)}
          interestRate={interestRate.value}
          loanTerm={loanTerm.value}
          interestOnlyPeriod={interestOnlyPeriod.value}
          isInterestOnly={isInterestOnly}
          resetCalculator={resetCalculator}
        />
      ) : (
        <InputsView
          loanAmount={loanAmount.inputAttrs}
          interestRate={interestRate.inputAttrs}
          loanTerm={loanTerm.inputAttrs}
          interestOnlyPeriod={interestOnlyPeriod.inputAttrs}
          isInterestOnly={{ isInterestOnly, setIsInterestOnly }}
          calculateMonthlyRepayment={calculateMonthlyRepayment}
        />
      )}
    </Wrapper>
  );
};
